import type { MusicPresetVocal } from '@/constants/musicPresets';

// Pure helpers for the music generator's Style field, lyric section tags and
// "Reuse" of an earlier song. No React and no DB, so they are easy to test
// (scripts/test-music-presets.ts).

/** Style is one line: the stored song text is `${style}\n\n${lyrics}`. */
export function cleanStyleInput(value: string): string {
  return value.replace(/\s*\n+\s*/g, ' ');
}

export function splitStyleTags(style: string): string[] {
  return style
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);
}

const sameTag = (a: string, b: string) =>
  a.trim().toLowerCase() === b.trim().toLowerCase();

export function hasStyleTag(style: string, tag: string): boolean {
  return splitStyleTags(style).some((existing) => sameTag(existing, tag));
}

/**
 * Adds `tag` to the comma-separated style, or removes it when it is already
 * there. `overflow` is true when adding would pass `max` (style unchanged).
 */
export function toggleStyleTag(
  style: string,
  tag: string,
  max: number,
): { style: string; added: boolean; overflow: boolean } {
  const tags = splitStyleTags(style);
  if (tags.some((existing) => sameTag(existing, tag))) {
    return {
      style: tags.filter((existing) => !sameTag(existing, tag)).join(', '),
      added: false,
      overflow: false,
    };
  }
  const trimmed = style.trim().replace(/,\s*$/, '');
  const next = trimmed ? `${trimmed}, ${tag}` : tag;
  if (next.length > max) {
    return { style, added: false, overflow: true };
  }
  return { style: next, added: true, overflow: false };
}

/** Drops an `instrumental` tag, for when the user has written lyrics. */
export function removeInstrumentalTag(style: string): string {
  return splitStyleTags(style)
    .filter((tag) => !sameTag(tag, 'instrumental'))
    .join(', ');
}

/**
 * Inserts a `[Section]` line at the caret. The tag gets a line of its own and
 * the caret lands on the empty line after it, ready to type.
 */
export function insertLyricSection(
  text: string,
  selectionStart: number,
  selectionEnd: number,
  section: string,
): { text: string; caret: number } {
  const start = Math.max(0, Math.min(selectionStart, text.length));
  const end = Math.max(start, Math.min(selectionEnd, text.length));
  const before = text.slice(0, start);
  const after = text.slice(end);

  let prefix = '';
  if (before.length > 0 && !before.endsWith('\n')) prefix = '\n\n';
  else if (before.length > 0 && !before.endsWith('\n\n')) prefix = '\n';

  // The tag ends with a newline, so whatever followed the caret becomes the
  // first line of the new section.
  const inserted = `${prefix}[${section}]\n`;
  return {
    text: `${before}${inserted}${after}`,
    caret: before.length + inserted.length,
  };
}

export interface ReusableSong {
  mode: 'custom' | 'description';
  style: string;
  lyrics: string;
  prompt: string;
  negativeTags: string;
  instrumental: boolean;
  vocalGender: MusicPresetVocal;
  presetId: string | null;
}

type SongLike = {
  text?: string | null;
  metadata?: string | Record<string, any> | null;
};

function parseMetadata(metadata: SongLike['metadata']): Record<string, any> {
  if (!metadata) return {};
  if (typeof metadata === 'object') return metadata;
  try {
    const parsed = JSON.parse(metadata);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

/**
 * What a song was made from, to load it back into the form. Songs store
 * `${style}\n\n${lyrics}` (Custom Mode) or the description as `text`; newer
 * rows also keep `style` in metadata, which wins because a style typed
 * before styles became one line could contain a blank line.
 */
export function parseSongForReuse(song: SongLike): ReusableSong {
  const meta = parseMetadata(song.metadata);
  const text = (song.text || '').toString();
  const vocal: MusicPresetVocal =
    meta.vocalGender === 'm' || meta.vocalGender === 'f'
      ? meta.vocalGender
      : '';
  const base = {
    negativeTags:
      typeof meta.negativeTags === 'string' ? meta.negativeTags : '',
    instrumental: meta.instrumental === true,
    vocalGender: vocal,
    presetId: typeof meta.presetId === 'string' ? meta.presetId : null,
  };

  const looksCustom =
    meta.customMode === true ||
    typeof meta.style === 'string' ||
    // The second version's row lost the request fields before 2026-10-09.
    (meta.customMode === undefined &&
      meta.isVariation &&
      text.includes('\n\n'));

  if (looksCustom) {
    let style = typeof meta.style === 'string' ? meta.style : '';
    let lyrics = '';
    const split = text.indexOf('\n\n');
    if (style && text.startsWith(style)) {
      lyrics = text.slice(style.length).replace(/^\n\n/, '');
    } else if (split >= 0) {
      style = style || text.slice(0, split);
      lyrics = text.slice(split + 2);
    } else {
      style = style || text;
    }
    return {
      ...base,
      mode: 'custom',
      style: cleanStyleInput(style).trim(),
      lyrics,
      prompt: '',
    };
  }

  return {
    ...base,
    mode: 'description',
    style: '',
    lyrics: '',
    prompt: text,
  };
}

/** The user's most recent distinct Custom Mode styles, newest first. */
export function recentStyles(songs: SongLike[], limit = 6): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const song of songs) {
    const reuse = parseSongForReuse(song);
    if (reuse.mode !== 'custom') continue;
    const style = reuse.style.trim();
    if (!style) continue;
    const key = style.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(style);
    if (result.length >= limit) break;
  }
  return result;
}

/** Cuts at a word boundary so a text fits a plan's character limit. */
export function fitToLength(text: string, max: number): string {
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  return (lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).replace(
    /[\s,;:.-]+$/,
    '',
  );
}

/**
 * Cuts lyrics at the last line break that fits, so a song never starts or
 * ends mid-line. Falls back to a word boundary for one long line.
 */
export function fitLyricsToLength(text: string, max: number): string {
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  const lastBreak = cut.lastIndexOf('\n');
  if (lastBreak > max * 0.5) return cut.slice(0, lastBreak).trimEnd();
  return fitToLength(text, max);
}

const META_LINE =
  /^(title|artist|artista|genre|género|genero|bpm|key|tempo|style|estilo)\b[^:：]{0,20}[:：]/i;

/**
 * Lyrics from a lyrics template's output, ready for Custom Mode: section
 * headings become [Verse 1]-style tags, Markdown, separators and headings
 * go, and the title lines before the first section are dropped. Lines in
 * plain parentheses stay sung (ad-libs); bold or italic ones are headings
 * or performance notes, so they become tags like square-bracket lines.
 */
export function lyricsFromText(text: string): string {
  const lines: string[] = [];
  for (const raw of text.replace(/\r/g, '').split('\n')) {
    const trimmed = raw.trim();
    if (/^[-–—*_=]{3,}$/.test(trimmed)) continue;
    if (/^#+\s/.test(trimmed)) continue;
    const styled = /^(\*\*|__|\*)/.test(trimmed);
    const line = trimmed
      .replace(/\*\*|__/g, '')
      .replace(/^\*(.+)\*$/, '$1')
      .trim();
    if (META_LINE.test(line)) continue;
    const bracket = line.match(/^\[\s*([^\]]+?)\s*\]\s*:?$/);
    const paren = styled ? line.match(/^\(\s*([^)]+?)\s*\)\s*:?$/) : null;
    const tag = bracket?.[1] ?? paren?.[1];
    if (tag) {
      // "[Verse 1 – whispered]" keeps "Verse 1"; Suno reads tags, not notes.
      lines.push(`[${tag.split(/\s+[–—-]\s+/)[0].trim()}]`);
      continue;
    }
    lines.push(line);
  }

  // Short lines before the first section are the title (and artist).
  const firstTag = lines.findIndex((line) => /^\[[^\]]+\]$/.test(line));
  if (firstTag > 0) {
    const before = lines.slice(0, firstTag).filter(Boolean);
    if (before.length <= 2 && before.every((line) => line.length <= 60)) {
      lines.splice(0, firstTag);
    }
  }

  return lines
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
